/** Rifle fire and grenades. */

import { bark, makeNoise } from '../alert';
import { type Ctx, isAlive } from '../ctx';
import { atan2, cos, PI, sin } from '../math';
import { spawnBullet } from '../projectiles';
import { byThreat, ENEMY, GRENADE, NOISE, secs, TACTICS, THREAT } from '../tuning';
import type { Enemy } from '../types';

const DEG = PI / 180;

export function shootAtRanger(ctx: Ctx, e: Enemy): void {
  const r = ctx.world.ranger;
  const threat = ctx.statics.def.threat;
  const manned = e.mans !== null;
  // The scientist, the general's aide: a man with nothing in his hands (`SpawnDef.unarmed`).
  if (e.unarmed === true) return;
  if (e.fireCooldown > 0 || e.suppressTicks > 0) return;
  if (e.burstLeft <= 0) {
    e.burstLeft = manned ? ENEMY.emplacementBurst : ENEMY.burst;
    const pause = byThreat(threat, THREAT.burstPauseSeconds[0], THREAT.burstPauseSeconds[1]);
    e.fireCooldown = secs(pause * (0.7 + ctx.rng.float() * 0.6));
    return;
  }
  const dx = r.x - e.x;
  const dz = r.z - e.z;
  const nerves = e.persona === 'jumpy' ? 1.3 : e.persona === 'steady' ? 0.9 : 1;
  const spread = byThreat(threat, THREAT.aimErrorDeg[0], THREAT.aimErrorDeg[1]) * (manned ? 0.7 : 1) * nerves * DEG;
  const a = atan2(dx, dz) + (ctx.rng.float() + ctx.rng.float() - 1) * spread;
  const dirX = sin(a);
  const dirZ = cos(a);
  spawnBullet(ctx, false, e.x + dirX * 0.6, e.z + dirZ * 0.6, dirX, dirZ, ENEMY.bulletRange, e.mans ?? -1, manned);
  e.burstLeft--;
  e.fireCooldown = manned ? ENEMY.emplacementTicksPerShot : ENEMY.ticksPerShot;
  e.sinceShot = 0;
  // His own fire is loud too: the garrison hears a fight, whoever started it.
  makeNoise(ctx, e.x, e.z, NOISE.enemyShot, 'gunfire');
}

/** Lost sight of a ranger who went to ground nearby: flush him out (02 §9.1). */
export function maybeThrowGrenade(ctx: Ctx, e: Enemy): boolean {
  if (e.grenades <= 0 || e.suppressTicks > 0) return false;
  if (e.lostTicks < TACTICS.grenadeAfterLostTicks || e.lostTicks > TACTICS.grenadeAfterLostTicks * 3) return false;
  const dx = e.targetX - e.x;
  const dz = e.targetZ - e.z;
  const d = Math.sqrt(dx * dx + dz * dz);
  if (d < 8 || d > GRENADE.maxRange) return false;
  // Never onto his own side.
  for (const o of ctx.world.enemies) {
    if (!isAlive(o) || o.id === e.id) continue;
    if ((o.x - e.targetX) * (o.x - e.targetX) + (o.z - e.targetZ) * (o.z - e.targetZ) < GRENADE.radius * GRENADE.radius) return false;
  }
  const w = ctx.world;
  e.grenades--;
  w.thrown.push({
    id: w.nextId++, kind: 'grenade', fromPlayer: false,
    startX: e.x, startZ: e.z, endX: e.targetX, endZ: e.targetZ,
    flightTicks: 0, flightTotal: GRENADE.flightTicks, fuseTicks: GRENADE.fuseTicks,
    x: e.x, z: e.z, y: 1.4,
  });
  ctx.events.push({ type: 'throw', x: e.x, z: e.z });
  bark(ctx, e, 'grenade');
  return true;
}
